/**
 * SaveScreen / ReviewScreen (SK-SAVE, T-11/T-12/T-13, DS-1g-05 §5.1) —
 * AC-1g-04, AC-1g-21, AC-1g-06, AC-1g-14, AC-1g-17.
 *
 * Extraction-derived fields (room, description, item text) rendered with
 * their `source_refs` and a confirm affordance; **ActionDecisionToggle**
 * (toggle=Action → Skill #2 `upsert`, toggle=Decision → Skill #3
 * `raise`/`resolve`); **ResolveDecisionPicker** (open decisions via
 * Skill #3 `view`, human picks, rationale required — AC-1g-21). Judgment
 * fields (criticality, owner — T-12) and destination flags (Daily/Weekly/
 * Internal-only/Client — T-12/T-13, serialized via `serializeDest()`) are
 * human-set with NO default and NO AI-sourced value; their state is
 * independent of (never derived from) `extraction` — the two flows share
 * no code path (grep target TW-1g-JUDGMENT-PREFILL / AC-1g-06, see
 * save-screen.dom.test.tsx). **#32: the transcript renders here as an
 * EDITABLE field** (rule #2 "confirm/correct" the heard text) when
 * `transcript` is supplied (omitted for a photo-only save; "" for an
 * empty-STT voice capture so the blank field still renders) — the old
 * standalone TranscribingScreen editor interstitial is gone. Edits
 * live-mirror out via `onTranscriptChange` onto `payload.transcript`.
 *
 * AC-1g-04: `canRoute` is the ONE locus deciding whether Save is
 * reachable — false until the human has made an explicit toggle choice
 * (and, on the decision/resolve branch, picked a candidate and typed a
 * rationale). No route is emitted without that toggle; this file contains no
 * code path that PICKS a toggle value. `initialToggle` is not a counterexample:
 * it is a value a human already tapped on this same screen, read back off the
 * saved entry so a re-open shows it — the caller supplies it and only ever for
 * an entry that is already routed. A fresh capture still starts blank.
 *
 * AC-1g-17 (T-13): the *Client report* flag is never a single tap. Tap 1
 * (`save-screen-dest-client`) is an affirmative CHOICE, not a set — it
 * only opens the second, distinct confirm
 * (`save-screen-client-confirm-yes` / `-cancel`). `clientConfirmed`
 * (fed into `serializeDest`'s `client`) is set ONLY by that second
 * confirm; there is no code path — no default, no preselection, no
 * swipe-through — that sets it from the first tap alone. Affirming the
 * second confirm also fires `onClientGuardFired` (the client-guard-fired
 * SIGNAL T-28 turns into a countable server-side event; see B-EMIT-GAP —
 * this build only emits the signal, T-28 owns the wire emission).
 *
 * T-10 (DS-1g-03 §3.2, AC-1g-16 client leg): the offline ask-back fork's
 * option (a) — a LOCAL context-block save-gate, not a server round-trip.
 * When the capture group carries a photo part (`hasPhotoPart`), the
 * context block ("what room is this?" + "what do you want done with
 * it?") becomes REQUIRED: `canRoute` gains an ADDITIONAL condition (both
 * answers non-empty) alongside the existing toggle condition, so a
 * context-free photo can never enable Save (never reaches HELD, never
 * the wire). This is a gate on the capture group rendered INLINE on this
 * same screen — never a modal/dialog (AC-1g-24's zero-added-dialogs
 * audit stays intact). Answers are exposed live via `onContextChange`
 * (mirrors `onDestChange`'s live-mirror pattern) for the caller that
 * owns outbox/Event construction (`payload.anchor_text` + room context,
 * per Skill #1's `FieldLogInput` — this component has no outbox
 * opinion, matching the rest of its architecture). The locked 1d
 * bare-photo server ASK remains the fail-closed backstop only, never
 * relied upon by this client-side gate.
 */

import type { ActionDecisionValue } from "./ActionDecisionToggle";
import type { Decision } from "../slice/decision-slice";
import type { DestChoice, DestMode, SerializedDest } from "../triage/serialize-dest";
import { BackLink } from "./BackLink";
import { SaveDestinations } from "./SaveDestinations";
import { SaveFooter } from "./SaveFooter";
import { SavePhotoContext } from "./SavePhotoContext";
import { SaveRouting } from "./SaveRouting";
import { SaveTags } from "./SaveTags";
import { SaveTranscript } from "./SaveTranscript";
import { useSaveScreenState } from "./save-screen-state";
import {
  EXTRACTION_KEYS,
  type DecisionMode,
  type ExplicitRoutingChoice,
  type RoutingDetection,
  type SaveScreenContext,
  type SaveScreenExtraction,
} from "../triage/save-screen-types";
export { PHASE_OPTIONS } from "../read/tag-filter";

export interface SaveScreenProps {
  extraction: SaveScreenExtraction;
  /** Open decisions from Skill #3's collapsed `view` read — handed to
   * ResolveDecisionPicker as-is (this component has no read-transport
   * opinion, matching DecisionLogView/T-18's convention). */
  openDecisions: Decision[];
  /** #32 (rule #2 "confirm/correct"): the capture's transcript, rendered on
   * this screen as an EDITABLE field so the human can correct the STT text
   * before it commits (rides `payload.transcript`). Omitted (undefined) when
   * the capture has no transcript at all — a photo-only save, or a video whose
   * audio is transcribed server-side later ("transcript pending"). An
   * empty-STT voice capture passes "" (NOT undefined) so the blank field still
   * renders for the human to type what they said. Lexicon (1j) correction now
   * happens server-side (coordinator /mark/transcribe), so the PWA receives
   * already-corrected text and there is no correction-span input to show. */
  transcript?: string;
  /** §9a.2: STT flagged this decode low-confidence — surfaces a "please check
   * this" note above the editable transcript. */
  lowConfidence?: boolean;
  /** Optional output of the existing governed review detector. Ordinary notes
   * have no detection. A non-empty detection gates Save until a human routes it
   * or explicitly keeps it only in the Daily Log. */
  routingDetection?: RoutingDetection;
  /** Default-off BUI-295 gate. Detection data is ignored unless explicitly enabled. */
  inferredRoutingSuggestionsEnabled?: boolean;
  /** True while edited text is being checked by the governed detector. Save is
   * fail-closed: only a fresh result bound to the exact text clears this state. */
  routingReviewPending?: boolean;
  /** Durable review recovery seed. It reflects a prior human choice; detection
   * itself must never populate this value. */
  initialRoutingChoice?: ExplicitRoutingChoice;
  onRoutingChoiceChange?: (choice: ExplicitRoutingChoice | null) => void;
  /** Live-mirror of edits to the transcript field (mirrors onDestChange/
   * onContextChange/onJudgmentChange). The caller carries the edited value onto
   * the committed Event's `payload.transcript`. Optional. */
  onTranscriptChange?: (transcript: string) => void;
  /** A blank voice transcript whose audio is still durable on-device can be
   * sent through the same idempotent upload/transcribe leg again. */
  canRetryTranscription?: boolean;
  onRetryTranscription?: () => void;
  /** T-10, AC-1g-16: true when this capture group carries a photo part —
   * makes the context block REQUIRED before Save is reachable. Omitted/
   * false leaves the pre-existing (photo-free) behavior unchanged. */
  hasPhotoPart?: boolean;
  /** #39-save: the in-flight `primary_photo` blob(s) for THIS capture, still
   * in-memory (PRE-commit — not yet in IndexedDB, so the IDB-reading
   * CaptureThumbs would render blank here). Rendered as an object-URL <img>
   * strip so the human sees the photo is being carried into the save (Red's
   * ask: "the thumbnail should be there so the user knows it's getting
   * transferred"), matching the daily-log thumbnail look. Object URLs are
   * built ONCE on mount and revoked (guarded) on unmount, exactly like
   * useCaptureThumbnails. Omitted/empty for a voice-only capture → no strip. */
  photoBlobs?: Blob[];
  /** AC-1g-07 / TW-1g-PROJECT-INFER: the user-selected, RBAC-scoped
   * project_ref (or `null` until the human picks one). Save can NEVER
   * route without it — `canRoute` gains a `project_ref !== null` condition
   * alongside the toggle/context gates, so a project-less capture never
   * reaches HELD (never the wire). This is EXCLUSIVELY the value threaded
   * from ProjectSelector via `selectedProjectRef()`; this component never
   * infers or defaults it. */
  project_ref: string | null;
  /** §align: the spec's save-screen-back — cancel this capture and return to
   * Record (discarding the draft). Optional so unit tests may omit it. */
  onBack?: () => void;
  /** Where the back link returns to (default "Record"); BackLink adds the ‹. */
  saveBackLabel?: string;
  onRouteAction: () => void;
  onRouteDecisionRaise: () => void;
  /** `decision_ref` is the picked decision's CAPTURE id (`Decision.id`) — the
   * identity Skill #3's resolve looks a canonical up by. See
   * ResolveDecisionPicker.onSelect. */
  onRouteDecisionResolve: (decision_ref: string, rationale: string) => void;
  /** §7.11: Type (Action/Decision) is OPTIONAL. When Save is pressed with no
   * toggle chosen, the capture commits with NO route — a plain
   * `daily_field_log_capture` record. Optional: a unit test that doesn't
   * exercise the untyped path may omit it. */
  onCommitUntyped?: () => void;
  /** Fired whenever the destination-flag group changes, carrying the
   * already-serialized wire shape (T-12, AC-1g-14). Optional: a caller
   * that doesn't care about destination routing yet may omit it. */
  onDestChange?: (dest: SerializedDest) => void;
  /** Fired ONLY when the client-report second confirm is affirmed
   * (T-13, AC-1g-17) — the client-guard-fired SIGNAL. Optional. */
  onClientGuardFired?: () => void;
  /** Surface a gated-control explanation (the shell's coming-soon modal). Used
   * by the client-report control while `CLIENT_REPORT_ENABLED` is false, so a
   * disabled affordance is never a silent dead tap. */
  onComingSoon?: (message: string) => void;
  /** Live-mirror of the T-10 context-block answers, fired on every
   * change (mirrors `onDestChange`'s pattern). Optional. */
  onContextChange?: (context: SaveScreenContext) => void;
  /** §7.11 / judgment-emit: live-mirror of the human-set judgment fields
   * (criticality tier + owner), `null` when unset/blank — so the caller can
   * carry them onto the emitted Event payload (the backend `ActionItemInput`
   * already expects `criticality_tier`/`owner`). NEVER seeded from
   * `extraction` (AC-1g-06). Mirrors onDestChange/onContextChange. Optional. */
  onJudgmentChange?: (judgment: {
    criticality_tier: string | null;
    owner: string | null;
    /** M-TAG1 (BUI-92): the human-set first-tier tags. `phase` is one of the
     * PHASE_OPTIONS ids (`"0".."9"`); `trade` is free-text (mirrors the Master
     * List detail Trade input). Both `null` until this human sets them, and
     * dirty-tracked exactly like criticality/owner — absent means unchanged. */
    phase: string | null;
    trade: string | null;
    decides: string | null;
    room: string | null;
    decides_touched: boolean;
    room_touched: boolean;
  }) => void;
  /** §4.3 re-open-from-log: "re-opens the save screen, every field/flag as it
   * was". These seed the screen's own state ONCE on mount when an ALREADY-SAVED
   * entry is re-opened from the Daily Log, so a super editing yesterday's entry
   * sees what they wrote rather than a blank form they have to re-answer.
   *
   * They are NOT extraction and NOT defaults: every value here was typed or
   * tapped by a human on this same screen earlier (AC-1g-06 — the app still
   * never guesses a judgment call). A fresh capture passes none of them. */
  initialContext?: SaveScreenContext;
  initialJudgment?: {
    criticality_tier: string | null;
    owner: string | null;
    /** M-TAG1 (BUI-92): re-open seed for the first-tier tags, read back off the
     * entry's own payload (a human tapped/typed these on this same screen
     * earlier — never AI-seeded). Absent on a fresh capture. */
    phase?: string | null;
    trade?: string | null;
    decides?: string | null;
    room?: string | null;
  };
  /** The destination flags as previously affirmed. `client: true` is carried
   * back WITHOUT re-running the §4.7 double confirm, and that is correct rather
   * than a bypass: the gate guards opting IN, and this human already did on this
   * entry. Turning it back off stays a single direct tap. */
  initialDest?: { choice: DestChoice; client: boolean };
  /** The Type this entry was already routed as, so re-opening an Action shows
   * "Action" pressed instead of an empty toggle. Same class of misread as
   * `initialDest` was, and the same answer: the screen must show what is
   * STORED, not a blank form.
   *
   * NOT a default and not a guess — it is read back off the entry's own
   * `payload.route`, which only a human tap could have written (AC-1g-06 /
   * §4.7's no-default rule is about the app never CHOOSING a type; reflecting
   * one a human already chose is the opposite of that).
   *
   * Decision raises are restored together with their mode. Resolve is not
   * restored because its candidate/rationale are not available on this record. */
  initialToggle?: ActionDecisionValue;
  initialDecisionMode?: Extract<DecisionMode, "raise">;
  /** Which emission this screen is composing. `edit` is what lets clearing every
   * destination box MEAN something — see `serializeDest`'s DestMode. Defaults to
   * `create`, so a fresh capture is unchanged. */
  destMode?: DestMode;
  /** §4.3 + Red's ruling ("keep Delete on the log detail"): present ONLY when
   * this screen was re-opened from a saved entry. The caller decides what it
   * means by state — a genuine local removal pre-wire, a terminal status carrier
   * post-finalize (never a row deletion). Absent on a fresh capture, where there
   * is nothing to delete. */
  onDeleteEntry?: () => void;
  /** A save is in progress: Save shows "Saving…" and ignores further taps. */
  saving?: boolean;
  /** Why the last save didn't land, shown next to Save. Save stays tappable so
   * the human can retry; a failed save must never be a silent dead tap. */
  saveError?: string | null;
}

export function SaveScreen(props: SaveScreenProps) {
  const {
    extraction,
    openDecisions,
    transcript,
    lowConfidence,
    canRetryTranscription = false,
    onRetryTranscription,
    hasPhotoPart,
    saving = false,
    saveError = null,
    project_ref,
    onBack,
    saveBackLabel,
    onComingSoon,
    onDeleteEntry,
  } = props;
  const {
    toggle,
    logOnly,
    transcriptDraft,
    decisionMode,
    setDecisionMode,
    selectedDecisionRef,
    setSelectedDecisionRef,
    rationale,
    setRationale,
    criticalityTier,
    ownerDraft,
    phaseDraft,
    tradeDraft,
    decidesDraft,
    roomTagDraft,
    destChoice,
    clientPending,
    clientConfirmed,
    photoUrls,
    contextRoom,
    contextIntent,
    routingReviewPending,
    activeRoutingDetection,
    explicitRoutingChoice,
    canRoute,
    handleTranscriptInput,
    handleContextRoomInput,
    handleContextIntentInput,
    handleCriticalityChip,
    handleOwnerInput,
    handlePhaseChange,
    handleTradeInput,
    handleDecidesChange,
    handleRoomTagInput,
    handleInternalOnlyToggle,
    handleDailyToggle,
    handleWeeklyToggle,
    handleClientFirstTap,
    handleClientConfirmYes,
    handleClientConfirmCancel,
    handleToggleChange,
    handleLogOnlyChoice,
    handleSave,
  } = useSaveScreenState(props);

  return (
    <div data-skin-slot="SK-SAVE" data-testid="save-screen">
      {/* §align (spec 3 Summary): top back row — cancel the capture back to
          Record. No bottom Close anywhere; this is the only exit. */}
      <div style="display:flex;align-items:center;justify-content:space-between">
        <BackLink label={saveBackLabel ?? "Record"} testid="save-screen-back" onBack={() => onBack?.()} />
      </div>
      <section data-testid="save-screen-extraction">
        {EXTRACTION_KEYS.map((key) => {
          const field = extraction[key];
          if (!field) return null;
          return (
            <div key={key} data-testid={`save-screen-field-${key}`}>
              <span data-testid={`save-screen-field-${key}-value`}>{field.value}</span>
              <span data-testid={`save-screen-field-${key}-source-refs`}>{field.source_refs.join(",")}</span>
            </div>
          );
        })}
        <label>
          <input type="checkbox" data-testid="save-screen-confirm-extraction" />
          Confirm
        </label>
      </section>

      <SaveTranscript
        transcript={transcript}
        transcriptDraft={transcriptDraft}
        handleTranscriptInput={handleTranscriptInput}
        lowConfidence={lowConfidence}
        canRetryTranscription={canRetryTranscription}
        onRetryTranscription={onRetryTranscription}
      />

      <SavePhotoContext
        photoUrls={photoUrls}
        hasPhotoPart={hasPhotoPart}
        contextRoom={contextRoom}
        contextIntent={contextIntent}
        handleContextRoomInput={handleContextRoomInput}
        handleContextIntentInput={handleContextIntentInput}
      />

      <SaveRouting
        routingReviewPending={routingReviewPending}
        activeRoutingDetection={activeRoutingDetection}
        logOnly={logOnly}
        handleLogOnlyChoice={handleLogOnlyChoice}
        toggle={toggle}
        handleToggleChange={handleToggleChange}
        decisionMode={decisionMode}
        setDecisionMode={setDecisionMode}
        openDecisions={openDecisions}
        selectedDecisionRef={selectedDecisionRef}
        setSelectedDecisionRef={setSelectedDecisionRef}
        rationale={rationale}
        setRationale={setRationale}
      />

      <SaveTags
        criticalityTier={criticalityTier}
        ownerDraft={ownerDraft}
        phaseDraft={phaseDraft}
        tradeDraft={tradeDraft}
        decidesDraft={decidesDraft}
        roomTagDraft={roomTagDraft}
        hasPhotoPart={hasPhotoPart}
        handleCriticalityChip={handleCriticalityChip}
        handleOwnerInput={handleOwnerInput}
        handlePhaseChange={handlePhaseChange}
        handleTradeInput={handleTradeInput}
        handleDecidesChange={handleDecidesChange}
        handleRoomTagInput={handleRoomTagInput}
      />

      <SaveDestinations
        destChoice={destChoice}
        clientPending={clientPending}
        clientConfirmed={clientConfirmed}
        handleDailyToggle={handleDailyToggle}
        handleWeeklyToggle={handleWeeklyToggle}
        handleInternalOnlyToggle={handleInternalOnlyToggle}
        handleClientFirstTap={handleClientFirstTap}
        handleClientConfirmYes={handleClientConfirmYes}
        handleClientConfirmCancel={handleClientConfirmCancel}
        onComingSoon={onComingSoon}
      />

      <SaveFooter
        project_ref={project_ref}
        canRoute={canRoute}
        saving={saving}
        routingReviewPending={routingReviewPending}
        activeRoutingDetection={activeRoutingDetection}
        explicitRoutingChoice={explicitRoutingChoice}
        handleSave={handleSave}
        saveError={saveError}
        onDeleteEntry={onDeleteEntry}
      />
    </div>
  );
}
